import { Skeleton } from '@/components/ui/skeleton';

interface MatchListSkeletonProps {
  count?: number;
}

/**
 * Shimmer loading skeleton for match lists.
 * Shows realistic placeholders during data fetching.
 */
export function MatchListSkeleton({ count = 3 }: MatchListSkeletonProps) {
  return (
    <div className="space-y-6 sm:space-y-8">
      {Array.from({ length: count }).map((_, leagueIndex) => (
        <div 
          key={leagueIndex} 
          className="space-y-3 sm:space-y-4 animate-fade-in"
          style={{ animationDelay: `${leagueIndex * 100}ms` }}
        >
          {/* League header skeleton */}
          <div className="flex items-center gap-3 px-1 py-2">
            <Skeleton className="w-6 h-6 sm:w-7 sm:h-7 rounded-md" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-36 sm:w-44" />
              <Skeleton className="h-3 w-20 sm:w-24" />
            </div>
          </div>

          {/* Match cards skeleton */}
          <div className="space-y-2.5 sm:space-y-3">
            {[1, 2].map((matchIndex) => (
              <MatchCardSkeleton 
                key={matchIndex} 
                delay={(leagueIndex * 100) + (matchIndex * 50)} 
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface MatchCardSkeletonProps {
  delay?: number;
}

/**
 * Individual match card skeleton with shimmer effect.
 */
export function MatchCardSkeleton({ delay = 0 }: MatchCardSkeletonProps) {
  return (
    <div 
      className="bg-card rounded-xl p-4 sm:p-5 border border-border/50 animate-fade-in"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Top Row */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>

      {/* Teams Row */}
      <div className="flex items-center justify-between gap-3 sm:gap-4">
        {/* Home Team */}
        <div className="flex-1 flex items-center gap-3 sm:gap-4">
          <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0" />
          <Skeleton className="h-4 w-24 sm:w-32" />
        </div>

        {/* Score */}
        <Skeleton className="h-8 w-16 rounded-lg shrink-0" />

        {/* Away Team */}
        <div className="flex-1 flex items-center justify-end gap-3 sm:gap-4">
          <Skeleton className="h-4 w-20 sm:w-28" />
          <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0" />
        </div>
      </div>

      {/* Bottom Row */}
      <div className="mt-3 sm:mt-4 pt-3 border-t border-border/30">
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
  );
}

/**
 * Prediction card skeleton for match detail page
 */
export function PredictionSkeleton() {
  return (
    <div className="bg-card rounded-xl p-5 sm:p-6 border border-border/50 animate-fade-in space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-5 w-32" />
        </div>
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>

      {/* Win probability bar */}
      <Skeleton className="h-10 w-full rounded-lg" />

      {/* Analysis box */}
      <div className="p-4 rounded-xl bg-muted/30 space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>

      {/* Tips */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    </div>
  );
}

/**
 * Odds skeleton for when odds are loading
 */
export function OddsSkeleton() {
  return (
    <div className="flex items-center gap-2 animate-pulse-subtle">
      <Skeleton className="h-8 w-14 rounded-md" />
      <Skeleton className="h-8 w-14 rounded-md" />
      <Skeleton className="h-8 w-14 rounded-md" />
    </div>
  );
}
